import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Save, Shield, X, Plus, RefreshCw } from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

export default function Settings() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [policy, setPolicy] = useState({
    min_length: 12,
    require_uppercase: true,
    require_lowercase: true,
    require_special_char: true,
    blacklist_words: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newWord, setNewWord] = useState("");
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => {
    api
      .get("/api/password-policy/")
      .then((res) => setPolicy(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!user?.is_admin) {
    return <Navigate to="/liveness/admin/client-apps" replace />;
  }

  const getBlacklistWords = () => {
    const words = policy.blacklist_words || "";
    return words
      .split(",")
      .map((w) => w.trim())
      .filter((w) => w);
  };

  const addWord = () => {
    if (!newWord.trim()) return;
    const current = getBlacklistWords();
    const word = newWord.trim().toLowerCase();
    if (current.includes(word)) {
      setNewWord("");
      return;
    }
    setPolicy({
      ...policy,
      blacklist_words: [...current, word].join(","),
    });
    setNewWord("");
  };

  const removeWord = (wordToRemove) => {
    const current = getBlacklistWords();
    setPolicy({
      ...policy,
      blacklist_words: current.filter((w) => w !== wordToRemove).join(","),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const res = await api.put("/api/password-policy/", policy);
      setPolicy(res.data);
      setSaveMsg(t("settings.saved"));
      setTimeout(() => setSaveMsg(""), 3000);
    } catch {
      setSaveMsg(t("error.saveSettings"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("settings.title")}
          </h1>
          <p className="text-muted-foreground mt-2 flex items-center gap-2">
            {t("settings.subtitle")}
            {loading && (
              <span className="flex items-center gap-1 text-xs">
                <RefreshCw className="w-3 h-3 animate-spin" />
                {t("common.loading")}
              </span>
            )}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving || loading}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? t("settings.saving") : t("settings.save")}
        </Button>
      </div>

      {saveMsg && (
        <div
          className={`rounded-lg p-3 text-sm ${
            saveMsg === t("settings.saved")
              ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
              : "bg-red-500/10 border border-red-500/30 text-red-400"
          }`}
        >
          {saveMsg}
        </div>
      )}

      {/* Password Policy */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-500" />
            <div>
              <h2 className="text-lg font-semibold">{t("policy.title")}</h2>
              <p className="text-sm text-muted-foreground">
                Configure os requisitos mínimos de senha para todos os usuários
                do sistema
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Min Length */}
            <div className="space-y-2">
              <Label htmlFor="min_length">{t("policy.minLength")}</Label>
              <Input
                id="min_length"
                type="number"
                min="8"
                max="40"
                value={policy.min_length}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    min_length: parseInt(e.target.value) || 8,
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                Número mínimo de caracteres (8-40).
              </p>
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div className="space-y-0.5">
                <Label>{t("policy.requireUpper")}</Label>
                <p className="text-xs text-muted-foreground">
                  A senha deve conter ao menos uma letra maiúscula (A-Z).
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setPolicy({
                    ...policy,
                    require_uppercase: !policy.require_uppercase,
                  })
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  policy.require_uppercase ? "bg-emerald-500" : "bg-muted"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    policy.require_uppercase
                      ? "translate-x-6"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div className="space-y-0.5">
                <Label>{t("policy.requireLower")}</Label>
                <p className="text-xs text-muted-foreground">
                  A senha deve conter ao menos uma letra minúscula (a-z).
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setPolicy({
                    ...policy,
                    require_lowercase: !policy.require_lowercase,
                  })
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  policy.require_lowercase ? "bg-emerald-500" : "bg-muted"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    policy.require_lowercase
                      ? "translate-x-6"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div className="space-y-0.5">
                <Label>{t("policy.requireSpecial")}</Label>
                <p className="text-xs text-muted-foreground">
                  A senha deve conter ao menos um caractere especial (!@#$%...).
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setPolicy({
                    ...policy,
                    require_special_char: !policy.require_special_char,
                  })
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  policy.require_special_char ? "bg-emerald-500" : "bg-muted"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    policy.require_special_char
                      ? "translate-x-6"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Blacklist Words */}
          <div className="space-y-3">
            <Label>{t("policy.blacklist")}</Label>
            <p className="text-xs text-muted-foreground">
              Palavras que não podem estar contidas na senha (verificação
              case-insensitive).
            </p>
            <div className="flex gap-2">
              <Input
                placeholder={t("policy.blacklistPlaceholder")}
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addWord()}
                className="max-w-xs"
              />
              <Button variant="outline" onClick={addWord}>
                <Plus className="w-4 h-4 mr-2" />
                {t("common.add")}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {getBlacklistWords().map((word) => (
                <span
                  key={word}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-muted text-muted-foreground border border-border"
                >
                  {word}
                  <button
                    onClick={() => removeWord(word)}
                    className="hover:bg-destructive/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {getBlacklistWords().length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhuma palavra bloqueada.
                </p>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4 mt-4">
            <h4 className="text-sm font-medium text-emerald-400 mb-2">
              Sobre a Política de Senha
            </h4>
            <ul className="text-xs text-emerald-300/80 space-y-1">
              <li>
                - Esta é a política padrão do sistema, aplicada a todos os
                usuários.
              </li>
              <li>
                - Empresas podem configurar políticas próprias que têm
                precedência sobre esta.
              </li>
              <li>
                - A verificação de blacklist é case-insensitive (admin = Admin =
                ADMIN).
              </li>
              <li>
                - Alterações se aplicam apenas a novas senhas; senhas existentes
                não são afetadas.
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
